import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { FlatOrgTreeTableNode } from 'src/utils/treeNode';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import Menu from '@mui/material/Menu';
import { useAuth } from 'src/state/auth';
import { Poppins } from 'src/common';
import { useNavigate } from 'react-router-dom';
import { useAcceptInviteMutation, useCancelRequestMutation, useRequestAccessMutation } from 'src/graphql/_generated';
import { useCache } from 'src/graphql/useCache';

const StyledMenu = styled(Menu)`
  transform: translateY(10px);
  & .MuiPaper-root {
    border-radius: 3px;
    box-shadow: 0px 0px 37px ${({ theme }) => theme.colors.shadow};
    background-color: ${({ theme }) => theme.colors.white};
  }
`;

const MenuItem = styled.li<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 4px 10px;
  list-style: none;

  span {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.cflowerBlue};

    ${({ $disabled }) =>
      $disabled &&
      css`
        opacity: 0.5;
        cursor: not-allowed;
      `}

    ${({ $disabled }) =>
      !$disabled &&
      css`
        &:hover {
          background: ${({ theme }) => theme.colors.gradient};
          -webkit-background-clip: text;
          color: transparent;
          cursor: pointer;
        }
      `}
  }
`;

interface TdmProps {
  data: FlatOrgTreeTableNode['data'];
  rowIndex?: number;
  closeModal: () => void;
  isOwnProfile: boolean;
}

type Action = 'O_SEE_SETTINGS' | 'O_REQUEST_ACCESS' | 'W_SEE_SETTINGS' | 'W_REQUEST_ACCESS' | 'W_CANCEL_REQUEST' | 'W_ACCEPT_INVITE';

export const Tdm: React.FC<TdmProps> = ({ data, rowIndex, closeModal, isOwnProfile }) => {
  const { profile, isPlatformAdmin } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableActions, setAvailableActions] = useState<Action[]>([]);
  const navigate = useNavigate();
  const { refetchQuery } = useCache();

  const resetCache = async () => await refetchQuery({ include: ['profile', 'flatOrganisations'] });

  const [requestAccess, { loading: isLoadingRequestAccess }] = useRequestAccessMutation({ onCompleted: resetCache });
  const [cancelRequest, { loading: isLoadingCancelRequest }] = useCancelRequestMutation({ onCompleted: resetCache });
  const [acceptInvite, { loading: isLoadingAcceptInvite }] = useAcceptInviteMutation({ onCompleted: resetCache });

  useEffect(() => {
    const actions: Action[] = [];

    if (data.recordType === 'O') {
      if (data.actionsOnThisOrgUnit?.canSeeSettings) {
        actions.push('O_SEE_SETTINGS');
      }
      if (isOwnProfile && data.actionsOnThisOrgUnit?.canRequest) {
        actions.push('O_REQUEST_ACCESS');
      }
    }

    if (data.recordType === 'W') {
      if (isOwnProfile) {
        if (profile?.user.requests.some((el) => el.workspaceId === data.id)) {
          actions.push('W_CANCEL_REQUEST');
        } else if (profile?.user.invites.some((el) => el.workspaceId === data.id)) {
          actions.push('W_ACCEPT_INVITE');
        } else if (data.actionsOnThisOrgUnit?.canRequest) {
          actions.push('W_REQUEST_ACCESS');
        }
      }

      if (data.actionsOnThisOrgUnit?.canSeeSettings) {
        actions.push('W_SEE_SETTINGS');
      }
    }

    setAvailableActions(actions);
  }, [data, isPlatformAdmin, profile, isOwnProfile]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuItemClick = (e: React.MouseEvent<HTMLLIElement>, action: Action) => {
    e.stopPropagation();
    setAnchorEl(null);

    switch (action) {
      case 'O_SEE_SETTINGS':
        navigate(`/organisation/${data.id}`);
        closeModal();
        break;

      case 'O_REQUEST_ACCESS':
        requestAccess({ variables: { organisationId: data.id } });
        break;

      case 'W_REQUEST_ACCESS':
        requestAccess({ variables: { workspaceId: data.id } });
        break;

      case 'W_CANCEL_REQUEST':
        cancelRequest({ variables: { workspaceId: data.id } });
        break;

      case 'W_ACCEPT_INVITE':
        acceptInvite({
          variables: { inviteId: profile?.user.invites.find((el) => el.workspaceId === data.id)?.id! },
        });
        break;

      case 'W_SEE_SETTINGS':
        navigate(`/workspace/${data.id}`);
        closeModal();
        break;
    }
  };

  const getActionLabel = (action: Action): string => {
    switch (action) {
      case 'O_SEE_SETTINGS':
        return 'View settings';
      case 'O_REQUEST_ACCESS':
        return 'Request access';
      case 'W_REQUEST_ACCESS':
        return 'Request access';
      case 'W_CANCEL_REQUEST':
        return 'Cancel request';
      case 'W_ACCEPT_INVITE':
        return 'Accept invite';
      case 'W_SEE_SETTINGS':
        return 'View settings';
      default:
        return '';
    }
  };

  const isDisabled = (action: Action): boolean => {
    switch (action) {
      case 'O_SEE_SETTINGS':
        return false;
      case 'O_REQUEST_ACCESS':
        return isLoadingRequestAccess;
      case 'W_REQUEST_ACCESS':
        return isLoadingRequestAccess;
      case 'W_CANCEL_REQUEST':
        return isLoadingCancelRequest;
      case 'W_ACCEPT_INVITE':
        return isLoadingAcceptInvite;
      default:
        return false;
    }
  };

  if (availableActions.length === 0) {
    return null;
  }

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginLeft: '4px' }} data-cy={`tdm-wrapper-${rowIndex}`}>
      <ThreeDots onClick={handleClick} alwaysActiveColor dataCy={`tdm-button-${rowIndex}`} />
      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        autoFocus={false}
      >
        <div>
          {availableActions.map((action) => (
            <MenuItem
              key={action}
              onClick={(e) => handleMenuItemClick(e, action)}
              data-cy={`tdm-menu-item-${action.toLowerCase().replace(/_/g, '-')}`}
              $disabled={isDisabled(action)}
            >
              <Poppins px={16}>{getActionLabel(action)}</Poppins>
            </MenuItem>
          ))}
        </div>
      </StyledMenu>
    </div>
  );
};
